/**
 * NavigationContext — shim entre o modelo navigate(screen) do protótipo
 * e o react-router-dom.
 *
 * CONTRATO PRESERVADO:
 *   const { navigate } = useNavigationContext();
 *   navigate('dashboard');   // idêntico ao setScreen('dashboard') do protótipo
 *   navigate('liveboard');   // navega para /execution/liveboard
 *
 * Por que o shim existe:
 *   O protótipo usa um único useState<screen> na raiz. Ao portar tela a tela
 *   incrementalmente, todas as telas já portadas chamam navigate('xyz') sem
 *   saber se 'xyz' está no router ou ainda no estado legado. O shim resolve
 *   o screen-key para o path correto e delega ao useNavigate() do router.
 *
 * Quando TODAS as telas estiverem portadas e a API cobrir cada uma, remover
 * o shim e usar useNavigate() diretamente — essa transição é transparente
 * para as telas, basta trocar o import do hook.
 */
import React, { createContext, useCallback, useContext } from 'react';
import { useNavigate as useRouterNavigate, useLocation } from 'react-router-dom';
import { screenToPath, pathToScreen } from './screenRoutes';

const NavigationContext = createContext(null);

/**
 * Provider — envolve toda a árvore autenticada (dentro do Router).
 * Expõe `navigate(screen)` e `currentScreen` (screen-key derivada do path).
 */
export function NavigationProvider({ children }) {
  const routerNavigate = useRouterNavigate();
  const location = useLocation();

  /**
   * navigate(screen) — shim compatível com o protótipo.
   * Aceita screen-keys ('dashboard', 'liveboard') ou paths absolutos ('/login').
   */
  const navigate = useCallback(
    (screen) => {
      // Se já é um path absoluto, usa direto
      const path = screen.startsWith('/') ? screen : screenToPath(screen);
      routerNavigate(path);
    },
    [routerNavigate],
  );

  // Deriva a screen-key a partir do path atual (para o Sidebar, Header, etc.)
  const currentScreen = pathToScreen(location.pathname);

  return (
    <NavigationContext.Provider value={{ navigate, currentScreen }}>
      {children}
    </NavigationContext.Provider>
  );
}

/**
 * useNavigationContext — hook para consumir o shim.
 *
 * Uso em qualquer componente portado do protótipo:
 *   const { navigate, currentScreen } = useNavigationContext();
 */
export function useNavigationContext() {
  const ctx = useContext(NavigationContext);
  if (!ctx) {
    throw new Error('useNavigationContext deve ser usado dentro de <NavigationProvider>');
  }
  return ctx;
}
